import { cn } from "@/lib/utils";
import { useState } from "react";
import { Lock } from "lucide-react";
import { getCardDesign, type CardVariant } from "@/lib/card-designs";

interface VirtualCardVisualProps {
  lastFour?: string | null;
  cardholderName?: string;
  expMonth?: number | null;
  expYear?: number | null;
  cardBrand?: string | null;
  status?: string;
  variant?: CardVariant;
  className?: string;
  showFullNumber?: boolean;
  fullNumber?: string;
  cvv?: string;
  interactive?: boolean;
}

export function VirtualCardVisual({
  lastFour,
  cardholderName = "CARDHOLDER NAME",
  expMonth,
  expYear,
  cardBrand,
  status,
  variant = 1,
  className,
  showFullNumber = false,
  fullNumber,
  cvv,
  interactive = true,
}: VirtualCardVisualProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!interactive) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setMousePosition({ x, y });
  };

  const formatCardNumber = () => {
    if (showFullNumber && fullNumber) {
      return fullNumber.replace(/(.{4})/g, "$1 ").trim();
    }
    return `•••• •••• •••• ${lastFour || "••••"}`;
  };

  const formatExpiry = () => {
    if (expMonth && expYear) {
      const month = expMonth.toString().padStart(2, "0");
      const year = expYear.toString().slice(-2);
      return `${month}/${year}`;
    }
    return "••/••";
  };

  const isInactive = status === "FROZEN" || status === "CANCELLED" || status === "EXPIRED";

  // Calculate 3D transform based on mouse position
  const rotateX = interactive && isHovered ? (mousePosition.y - 50) / 8 : 0;
  const rotateY = interactive && isHovered ? (50 - mousePosition.x) / 8 : 0;

  const design = getCardDesign(variant);
  const cardBackground = design.front;

  return (
    <div
      className={cn(
        "relative w-full max-w-[400px] aspect-[1.586/1] perspective-[1000px]",
        className
      )}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setMousePosition({ x: 50, y: 50 });
      }}
    >
      {/* Card container with 3D transform */}
      <div
        className={cn(
          "relative w-full h-full rounded-2xl overflow-hidden transition-all duration-300",
          "shadow-xl hover:shadow-2xl",
          isInactive && "grayscale opacity-75"
        )}
        style={{
          transform: `rotateX(${rotateX}deg) rotateY(${rotateY}deg)`,
          transformStyle: "preserve-3d",
        }}
      >
        {/* Card Background */}
        <img
          src={cardBackground}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* Frosted overlay for frozen/cancelled cards */}
        {isInactive && (
          <div className="absolute inset-0 bg-white/10 backdrop-blur-[1px]" />
        )}

        {/* Holographic shine effect on hover */}
        <div
          className={cn(
            "absolute inset-0 transition-opacity duration-300 pointer-events-none",
            isHovered ? "opacity-40" : "opacity-0"
          )}
          style={{
            background: `linear-gradient(
              ${105 + (mousePosition.x - 50) * 0.5}deg,
              transparent 0%,
              hsl(172 70% 60% / 0.15) 20%,
              hsl(43 96% 60% / 0.2) 40%,
              hsl(300 70% 60% / 0.15) 60%,
              hsl(172 70% 60% / 0.15) 80%,
              transparent 100%
            )`,
          }}
        />

        {/* Card content overlay - only variable data, card image has branding/chip/contactless baked in */}
        <div className="absolute inset-0 p-5 sm:p-6 flex flex-col justify-end text-white">
          {/* Status badge - top right */}
          {status && isInactive && (
            <div className="absolute top-4 right-4">
              <div className={cn(
                "px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide backdrop-blur-sm border flex items-center gap-1",
                status === "FROZEN" && "bg-blue-500/30 border-blue-400/30 text-blue-200",
                status === "CANCELLED" && "bg-red-500/30 border-red-400/30 text-red-200",
                status === "EXPIRED" && "bg-gray-500/30 border-gray-400/30 text-gray-200"
              )}>
                {status === "FROZEN" && <Lock className="h-3 w-3" />}
                {status}
              </div>
            </div>
          )}

          {/* Bottom section - Card details */}
          <div className="space-y-3">
            {/* Card number */}
            <div
              className="font-mono text-xl sm:text-2xl tracking-[0.15em] font-medium drop-shadow-[0_1px_3px_rgba(0,0,0,0.8)]"
            >
              {formatCardNumber()}
            </div>

            {/* Cardholder name and expiry row */}
            <div className="flex justify-between items-end">
              <div className="space-y-0.5">
                <div className="text-[9px] uppercase tracking-[0.2em] text-white/70 font-medium drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
                  Card Holder
                </div>
                <div
                  className="text-sm font-semibold uppercase tracking-wide truncate max-w-[160px] drop-shadow-[0_1px_3px_rgba(0,0,0,0.8)]"
                >
                  {cardholderName}
                </div>
              </div>

              <div className="text-right space-y-0.5">
                <div className="text-[9px] uppercase tracking-[0.2em] text-white/70 font-medium drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
                  Valid Thru
                </div>
                <div className="font-mono text-sm font-semibold drop-shadow-[0_1px_3px_rgba(0,0,0,0.8)]">
                  {formatExpiry()}
                </div>
              </div>

              {showFullNumber && cvv && (
                <div className="text-right space-y-0.5 ml-4">
                  <div className="text-[9px] uppercase tracking-[0.2em] text-white/70 font-medium drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
                    CVV
                  </div>
                  <div className="font-mono text-sm font-semibold drop-shadow-[0_1px_3px_rgba(0,0,0,0.8)]">
                    {cvv}
                  </div>
                </div>
              )}

              {/* Card network logo */}
              <div className="ml-4">
                <div className="flex items-center gap-1 opacity-90">
                  {(cardBrand?.toUpperCase() === "VISA" || !cardBrand) && (
                    <span className="text-xl font-bold italic tracking-tight drop-shadow-[0_1px_3px_rgba(0,0,0,0.8)]">
                      VISA
                    </span>
                  )}
                  {cardBrand?.toUpperCase() === "MASTERCARD" && (
                    <div className="flex -space-x-2">
                      <div className="w-6 h-6 rounded-full bg-red-500/90 shadow-md" />
                      <div className="w-6 h-6 rounded-full bg-amber-400/90 shadow-md" />
                    </div>
                  )}
                  {cardBrand?.toUpperCase() === "RUPAY" && (
                    <span className="text-lg font-bold drop-shadow-[0_1px_3px_rgba(0,0,0,0.8)]">
                      RuPay
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Premium border glow on hover */}
        <div
          className={cn(
            "absolute inset-0 rounded-2xl pointer-events-none transition-opacity duration-300",
            isHovered ? "opacity-100" : "opacity-0"
          )}
          style={{
            boxShadow: "inset 0 0 0 1px hsl(172 60% 50% / 0.3), 0 0 30px hsl(172 60% 40% / 0.15)",
          }}
        />
      </div>
    </div>
  );
}
